/**
 * Port of `repos/opencode/packages/session-ui/src/components/session-turn.tsx`, keyed on
 * `styles/components/session-turn.css` (vendored byte-identical).
 *
 * One turn: the user's message, then the assistant's parts, then — once the turn is done
 * and it edited files — the diffs group. The nesting matters as much as the attributes:
 * the stylesheet reaches assistant content through `session-turn-message-container`, so
 * flattening these wrappers loses the spacing even with every attribute still present.
 *
 * Upstream also owns scrolling here, through an auto-scroll hook on
 * `session-turn-content`. Ours lives one level up in `ChatSurface`, because the transcript
 * scrolls as a whole rather than per turn; `session-turn-content` is still emitted, since
 * the stylesheet lays out through it.
 */

import type { ReactNode } from "react";

import { Accordion } from "../opencode/primitives/accordion.tsx";
import { DiffChanges } from "../opencode/primitives/diff-changes.tsx";
import { TextShimmer } from "../ui/text-shimmer.tsx";

export interface TurnDiff {
  readonly file: string;
  readonly additions: number;
  readonly deletions: number;
  readonly body?: ReactNode;
}

export interface SessionTurnProps {
  readonly interactive?: boolean;
  /** The user's message for this turn. */
  message?: ReactNode;
  /** The assistant's parts. */
  children?: ReactNode;
  /** True while the turn is still producing output. */
  working?: boolean;
  /** Shown while the model is thinking and has emitted nothing yet. */
  thinkingLabel?: string;
  /** Files this turn changed. Rendered once the turn settles. */
  diffs?: readonly TurnDiff[];
  className?: string;
}

function directoryOf(path: string): string {
  const at = path.lastIndexOf("/");
  return at === -1 ? "" : path.slice(0, at + 1);
}

function filenameOf(path: string): string {
  const at = path.lastIndexOf("/");
  return at === -1 ? path : path.slice(at + 1);
}

function TurnDiffs({ diffs }: { diffs: readonly TurnDiff[] }) {
  const additions = diffs.reduce((total, diff) => total + diff.additions, 0);
  const deletions = diffs.reduce((total, diff) => total + diff.deletions, 0);

  return (
    <div data-slot="session-turn-diffs" data-component="session-turn-diffs-group">
      <div data-slot="session-turn-diffs-header">
        <span data-slot="session-turn-diffs-label">
          {diffs.length} file{diffs.length === 1 ? "" : "s"} changed
        </span>
        <DiffChanges changes={{ additions, deletions }} />
      </div>
      <div data-component="session-turn-diffs-content">
        <Accordion multiple data-scope="session-turn-diffs">
          {diffs.map((diff) => (
            <Accordion.Item key={diff.file} value={diff.file}>
              <Accordion.Header>
                <Accordion.Trigger>
                  <div data-slot="session-turn-diff-trigger">
                    <span data-slot="session-turn-diff-path">
                      {directoryOf(diff.file) ? (
                        // The directory is truncated from the left, which reverses a
                        // trailing slash without these marks: "src/" renders as "/src".
                        <span data-slot="session-turn-diff-directory">
                          {`\u202A${directoryOf(diff.file)}\u202C`}
                        </span>
                      ) : null}
                      <span data-slot="session-turn-diff-filename">
                        {filenameOf(diff.file)}
                      </span>
                    </span>
                    <div data-slot="session-turn-diff-meta">
                      <span data-slot="session-turn-diff-changes">
                        <DiffChanges
                          changes={{
                            additions: diff.additions,
                            deletions: diff.deletions
                          }}
                        />
                      </span>
                      <span data-slot="session-turn-diff-chevron" />
                    </div>
                  </div>
                </Accordion.Trigger>
              </Accordion.Header>
              <Accordion.Content>
                <div data-slot="session-turn-diff-view" data-scrollable="">
                  {diff.body}
                </div>
              </Accordion.Content>
            </Accordion.Item>
          ))}
        </Accordion>
      </div>
    </div>
  );
}

export function SessionTurn({
  message,
  children,
  working,
  interactive = false,
  thinkingLabel = "Thinking",
  diffs,
  className
}: SessionTurnProps) {
  // Streaming prose remains hidden while it is changing. Pending actions stay
  // accessible so an approval can unblock the open turn.
  const showDiffs = !working && diffs !== undefined && diffs.length > 0;

  return (
    <div data-component="session-turn" className={className}>
      <div data-slot="session-turn-content">
        <div data-slot="session-turn-message-container">
          {message ? (
            <div data-slot="session-turn-message-content" aria-live="off">
              {message}
            </div>
          ) : null}

          {children ? (
            <div data-slot="session-turn-assistant-content" aria-hidden={working && !interactive}>
              {children}
            </div>
          ) : null}

          {working && !children ? (
            <div data-slot="session-turn-thinking">
              <TextShimmer text={thinkingLabel} active />
            </div>
          ) : null}

          {showDiffs ? <TurnDiffs diffs={diffs} /> : null}
        </div>
      </div>
    </div>
  );
}
